import {FC, useEffect, useState} from "react";
import {VatRate} from "@traas/lib/src/api/dto/VatRate";
import {Ledger} from "@traas/lib/src/api/dto/Ledger";
import {useNavigate} from "react-router";
import {useApiService} from "@traas/lib/src/api/hooks/useApiService";
import config from "../../config";
import {CustomerRelationType, Relation} from "@traas/lib/src/api/dto/Relation";
import {ErrorBag, ValidationError} from "../../api/api";
import {faSave} from "@fortawesome/free-solid-svg-icons";
import {InputErrors} from "../../components/InputErrors";
import {Select} from "@traas/lib/src/components/form/Select";
import {ModalFooter} from "@traas/lib/src/components/layout/ModalProvider";
import {Toggle} from "@traas/lib/src/components/form/Toggle";
import {Input} from "@traas/lib/src/components/form/Input";
import {getToken} from "@ventumsoftware/react-toolkit";

export const AddRelationModal: FC<{vatRates: VatRate[], ledgerAccounts: Ledger[], onFinish?: (relation: Relation) => void}> = (props) => {
  const navigate = useNavigate()
  const { relationService, contactService, addressService } = useApiService(config, getToken, navigate)

  // Relations
  const [firstName, setFirstName] = useState("")
  const [middleName, setMiddlename] = useState("")
  const [lastName, setLastName] = useState("")
  const [name, setName] = useState<string>('')
  const [type, setType] = useState<CustomerRelationType|null>('business-small')
  const [phone, setPhone] = useState<string>('')
  const [locationStreet, setLocationStreet] = useState<string>('')
  const [locationHouseNumber, setLocationHouseNumber] = useState<string>('')
  const [locationHouseNumberAddition, setLocationHouseNumberAddition] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [postalcode, setPostalcode] = useState<string>('')
  const [web, setWeb] = useState<string>('')
  const [email, setEmail] = useState<string>('')
  const [paymentTerm, setPaymentTerm] = useState<number>(30)
  const [vatRateId, setVatRateId] = useState<string|null>(null)
  const [cocNumber, setCocNumber] = useState<string>('')
  const [vatNumber, setVatNumber] = useState<string>('')
  const [iban, setIban] = useState<string>('')
  const [isSupplier, setIsSupplier] = useState<boolean>(true)
  const [defaultLedgerAccount, setDefaultLedgerAccount] = useState<string>('-')

  const vatRateOptions = [
    ['-', 'Selecteer Tarief...'],
    ...props.vatRates.filter(v => v.enabled).map(vatRate => [vatRate.id, vatRate.name]),
  ]

  const ledgerOptions = [
    ['-', 'Selecteer Grootboekrekening...'],
    ...props.ledgerAccounts.filter(l => l.type === 'credit').map(vatRate => [vatRate.id, `${vatRate.code}: ${vatRate.description}`]),
  ]

  useEffect(() => {
    if (type === 'individual') {
      setName(middleName.trim().length > 0 ? `${firstName.trim()} ${middleName.trim()} ${lastName.trim()}` : `${firstName.trim()} ${lastName.trim()}`)
    }
  }, [firstName, lastName, middleName, type]);

  const [errors, setErrors] = useState<ErrorBag>({})

  const attemptComplete = () => {
    if (postalcode && locationHouseNumber) {
      addressService.lookup(postalcode, parseInt(locationHouseNumber)).then(result => {
        if (result.street) {
          setLocationStreet(result.street)
        }
        if (result.city) {
          setCity(result.city)
        }
      })
    }
  }


  const confirm = async () => {
    const relation = await relationService.add(
      name,
      type!,
      '',
      phone,
      locationStreet,
      locationHouseNumber,
      locationHouseNumberAddition,
      city,
      postalcode,
      web,
      email,
      paymentTerm,
      vatRateId,
      cocNumber,
      vatNumber,
      iban,
      isSupplier,
      defaultLedgerAccount === '-' ? null : defaultLedgerAccount,
    ).catch(e => {
        if (e instanceof ValidationError) {
          setErrors(old => ({...old, ...e.errors}))
        }
        throw e
      })
    if (type === 'individual') {
      await contactService.add(firstName, middleName, lastName, 'Particulier', phone, email, null, relation.id)
    }
    props.onFinish?.(relation)
  }

  return <>
    <div className={'space-y-3'}>
      {type !== null && <>
        {type === 'individual' ? <>
          <div className={'grid grid-cols-5 gap-3'}>
            <div className={'col-span-2'}>
              <Input type={'text'} required label={'Voornaam'} width={'full'} value={firstName} onChange={setFirstName} />
              <InputErrors errors={errors} field={'name'} />
            </div>
            <div>
              <Input type={'text'} label={'Tussenv.'} width={'full'} value={middleName} onChange={setMiddlename} />
            </div>
            <div className={'col-span-2'}>
              <Input type={'text'} required label={'Achternaam'} width={'full'} value={lastName} onChange={setLastName} />
            </div>
          </div>
        </> : <>
          <div className={'grid grid-cols-2 gap-3'}>
            <div className={'col-span-2'}>
              <Input type={'text'} label={'Bedrijfsnaam'} width={'full'} value={name} onChange={setName} />
              <InputErrors errors={errors} field={'name'} />
            </div>
            <div>
              <Input type={'text'} label={'KVK Nummer'} width={'full'} value={cocNumber} onChange={setCocNumber} />
              <InputErrors errors={errors} field={'coc_number'} />
            </div>
            <div>
              <Input type={'text'} label={'BTW Nummer'} width={'full'} value={vatNumber} onChange={setVatNumber} />
              <InputErrors errors={errors} field={'vat_number'} />
            </div>
          </div>
          <div>
            <Toggle label={'Is Leverancier'} size={'sm'} value={isSupplier} onChange={setIsSupplier} />
          </div>
          {isSupplier && (
            <Select options={Object.fromEntries(ledgerOptions)} label={'Standaard grootboekrekening'} value={defaultLedgerAccount} onChange={setDefaultLedgerAccount} />
          )}
        </>}
        <div>
          <Input type={'text'} label={'IBAN'} width={'full'} value={iban} onChange={setIban} />
          <InputErrors errors={errors} field={'iban'} />
        </div>

        <div className={'grid grid-cols-4 gap-3'}>
          <div className={'col-span-2'}>
            <Input type={'text'} label={'Postcode'} value={postalcode} onChange={(x) => setPostalcode(x.replaceAll(/[^a-zA-Z0-9]/g, ''))} onBlur={() => attemptComplete()} />
            <InputErrors errors={errors} field={'location_zipcode'} />
          </div>
          <div>
            <Input type={'text'} label={'Huisnummer'} width={'full'} value={locationHouseNumber} onChange={setLocationHouseNumber} onBlur={() => attemptComplete()} />
            <InputErrors errors={errors} field={'location_address_house_number'} />
          </div>
          <div>
            <Input type={'text'} label={'Toevoeging'} width={'full'} value={locationHouseNumberAddition} onChange={setLocationHouseNumberAddition} />
            <InputErrors errors={errors} field={'location_address_house_number_addition'} />
          </div>
        </div>

        <div className={'grid grid-cols-2 gap-3'}>
          <div>
            <Input type={'text'} label={'Straatnaam'} width={'full'} value={locationStreet} onChange={setLocationStreet} />
            <InputErrors errors={errors} field={'location_address_streetname'} />
          </div>
          <div>
            <Input type={'text'} label={'Stad'} value={city} onChange={setCity} />
            <InputErrors errors={errors} field={'location_city'} />
          </div>
        </div>
        <div className={'grid grid-cols-2 gap-3'}>
          <div>
            <Input type={'text'} label={'Telefoon'} value={phone} onChange={setPhone} />
            <InputErrors errors={errors} field={'phone'} />
          </div>
          <div>
            <Input type={'text'} label={'E-Mail'} value={email} onChange={setEmail} />
            <InputErrors errors={errors} field={'email'} />
          </div>
          <div>
            <Input type={'number'} label={'Betaaltermijn'} value={paymentTerm} onChange={setPaymentTerm} />
            <InputErrors errors={errors} field={'payment_term'} />
          </div>
          <div>
            <Select label={'Standaard BTW Tarief'} value={vatRateId === null ? '-' : vatRateId} onChange={(v) => setVatRateId(v === '-' ? null : v)} options={Object.fromEntries(vatRateOptions)} />
            <InputErrors errors={errors} field={'vat_rate_id'} />
          </div>
        </div>
      </>}
    </div>

    <ModalFooter text={'Opslaan'} icon={faSave} disabled={type === null || vatRateId === null || (type === 'individual' ? (firstName.length === 0 || lastName.length === 0) : (name.length === 0))} onSubmit={confirm} />
  </>
}